/**
 * The SignaPass mark — one geometry, four renderers.
 *
 * The mark is a signature: one unbroken stroke over three collected stamps.
 * It is drawn in four places that cannot share a renderer — two static
 * `icon.svg` files, a PNG rasteriser that runs in Node, a React component in
 * `@signapass/ui` and another in the landing page, which does not depend on
 * `ui`. Every one of them reads its numbers from here, so moving a dot is one
 * edit rather than five, and `logo.test.ts` fails if a copy is left behind.
 *
 * It lives in `shared` rather than in `packages/ui` for the same reason the
 * theme constants do: the landing page needs it and does not depend on `ui`.
 * Nothing here runs at import time or touches the DOM, so the API can carry it
 * in its module graph without noticing.
 */
/** The mark is drawn in a 120×120 box. Every number below is in those units. */
export const LOGO_VIEWBOX = 120;
/** The rounded square behind the mark. Only the app icons draw it — see below. */
export const LOGO_CORNER_RADIUS = 26.4;
/** Where the signature starts, at the bottom-left of the first upstroke. */
export const LOGO_SIGNATURE_START = [12, 76];
/**
 * The signature, as cubic Bézier segments: `[control1, control2, end]`, each
 * continuing from the previous segment's end.
 *
 * Held as numbers rather than as a `d` string because the Node rasteriser has
 * to flatten the curve to measure distance to it, and parsing path syntax to
 * get back to the numbers we started with would be the long way round.
 * `logoSignaturePath()` builds the `d` string the SVG renderers want.
 */
export const LOGO_SIGNATURE_CURVES = [
    [
        [18, 50],
        [30, 40],
        [36, 48],
    ],
    [
        [42, 56],
        [30, 70],
        [34, 76],
    ],
    [
        [40, 84],
        [52, 46],
        [58, 48],
    ],
    [
        [64, 50],
        [52, 72],
        [58, 78],
    ],
    [
        [64, 84],
        [76, 48],
        [82, 50],
    ],
    [
        [88, 52],
        [76, 74],
        [82, 78],
    ],
    [
        [88, 82],
        [98, 70],
        [104, 58],
    ],
];
/**
 * The signature's weight on a bare surface, where the stroke is the mark's
 * whole silhouette.
 *
 * Both caps and both joins are round, which is what lets the rasteriser treat
 * the stroke as "every point within half a width of the curve" — see
 * `logoSignaturePolyline`.
 */
export const LOGO_SIGNATURE_WIDTH = 8;
/**
 * The same stroke inside an app icon, one unit heavier.
 *
 * Reversed out of the ink plate the stroke optically thins; on a launcher icon
 * seen at 48 device pixels that is the difference between a signature and a
 * smudge.
 */
export const LOGO_ICON_SIGNATURE_WIDTH = 9;
export const LOGO_DOT_RADIUS = 5;
/** Visits collected, sitting under the signature like stamps under a name. */
export const LOGO_DOTS = [
    [36, 94],
    [60, 94],
    [84, 94],
];
/**
 * How much of an app icon's plate the mark fills.
 *
 * The mark is drawn to the edges of its own box, so on a plate it needs an
 * inset or it reads as a sticker trimmed to fit. 72% is also what keeps it
 * inside the maskable safe zone — see `logoMarkReach()`.
 */
export const LOGO_ICON_SCALE = 0.72;
/** The inset that centres the scaled mark on the plate. */
export const LOGO_ICON_OFFSET = (LOGO_VIEWBOX * (1 - LOGO_ICON_SCALE)) / 2;
/**
 * The mark's fixed colours — app icons only.
 *
 * These are raw hexes rather than tokens on purpose, and it is the one place
 * that is allowed (DESIGN-SYSTEM.md §2): a favicon, a launcher icon and a
 * home-screen icon are composited by the OS, which has never heard of
 * `prefers-color-scheme` and will not re-read a CSS variable. The mark carries
 * its own ink plate there so it survives whatever chrome it is dropped onto.
 *
 * The signature reverses to white on the plate rather than staying ink. The
 * in-app renderers below do the opposite — they drop the plate and take
 * `currentColor` — which is why neither of them imports this.
 */
export const LOGO_COLOURS = {
    plate: '#14122B',
    signature: '#FFFFFF',
    dot: '#5B4BE8',
};
export const LOGO_LABEL = 'SignaPass';
/**
 * The wordmark, split where its colour changes: ink, then violet.
 *
 * Split here rather than in each renderer so the two halves cannot drift into
 * "SignaPass" + "Pass" the next time somebody edits one of the two copies.
 * Joined they must equal `LOGO_LABEL`; `logo.test.ts` checks that.
 */
export const LOGO_WORDMARK = {
    lead: 'Signa',
    tail: 'Pass',
};
/**
 * A maskable Android icon must keep its content inside a circle of 40% of the
 * canvas; a round launcher mask clips anything outside it.
 */
export const LOGO_MASKABLE_SAFE_RATIO = 0.4;
/** The signature as an SVG path `d`, built from the numbers above. */
export function logoSignaturePath() {
    const [x, y] = LOGO_SIGNATURE_START;
    return LOGO_SIGNATURE_CURVES.reduce((d, [[c1x, c1y], [c2x, c2y], [ex, ey]]) => `${d} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${ex} ${ey}`, `M${x} ${y}`);
}
/**
 * The signature flattened to a polyline.
 *
 * Exists for the rasteriser, which has no path engine: because both caps and
 * both joins are round, the stroked shape is exactly the set of points within
 * `width / 2` of this polyline, so "am I on the stroke?" becomes a distance
 * test. `steps` is per curve segment; 24 puts the chord error far below one
 * device pixel at 512px.
 */
export function logoSignaturePolyline(steps = 24) {
    const points = [LOGO_SIGNATURE_START.slice()];
    let from = LOGO_SIGNATURE_START;
    for (const [c1, c2, end] of LOGO_SIGNATURE_CURVES) {
        for (let i = 1; i <= steps; i++) {
            const t = i / steps;
            const u = 1 - t;
            // Bernstein form, applied to both axes.
            const a = u * u * u;
            const b = 3 * u * u * t;
            const c = 3 * u * t * t;
            const d = t * t * t;
            points.push([
                a * from[0] + b * c1[0] + c * c2[0] + d * end[0],
                a * from[1] + b * c1[1] + c * c2[1] + d * end[1],
            ]);
        }
        from = end;
    }
    return points;
}
/**
 * How far the *plated* mark reaches from the centre of the icon, in viewBox
 * units — the number the maskable safe zone is checked against.
 *
 * Measured after the icon inset rather than on the bare mark, because the inset
 * is what makes the mark fit: undrawn, the signature's first upstroke sits 54
 * units from centre in a canvas whose safe circle is 48.
 */
export function logoMarkReach() {
    const centre = LOGO_VIEWBOX / 2;
    const toIcon = (v) => LOGO_ICON_OFFSET + v * LOGO_ICON_SCALE;
    const reach = (x, y, pad) => Math.hypot(toIcon(x) - centre, toIcon(y) - centre) + pad * LOGO_ICON_SCALE;
    return Math.max(...LOGO_DOTS.map(([x, y]) => reach(x, y, LOGO_DOT_RADIUS)), ...logoSignaturePolyline().map(([x, y]) => reach(x, y, LOGO_ICON_SIGNATURE_WIDTH / 2)));
}
/**
 * The plated mark as a standalone SVG document.
 *
 * This is the exact content of `apps/app/public/icon.svg` and
 * `apps/landing/src/app/icon.svg`. Both are static files that no build step
 * generates — a favicon has to exist before any JavaScript runs — so they are
 * committed copies, and `logo.test.ts` compares them against this byte for byte.
 */
export function logoMarkSvg() {
    const dots = LOGO_DOTS.map(([cx, cy]) => `    <circle cx="${cx}" cy="${cy}" r="${LOGO_DOT_RADIUS}" fill="${LOGO_COLOURS.dot}"/>`);
    return [
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${LOGO_VIEWBOX} ${LOGO_VIEWBOX}" role="img" aria-label="${LOGO_LABEL}">`,
        `  <rect width="${LOGO_VIEWBOX}" height="${LOGO_VIEWBOX}" rx="${LOGO_CORNER_RADIUS}" fill="${LOGO_COLOURS.plate}"/>`,
        `  <g transform="translate(${LOGO_ICON_OFFSET} ${LOGO_ICON_OFFSET}) scale(${LOGO_ICON_SCALE})">`,
        `    <path d="${logoSignaturePath()}" fill="none" stroke="${LOGO_COLOURS.signature}" stroke-width="${LOGO_ICON_SIGNATURE_WIDTH}" stroke-linecap="round" stroke-linejoin="round"/>`,
        ...dots,
        '  </g>',
        '</svg>',
        '',
    ].join('\n');
}
//# sourceMappingURL=logo.js.map